import './InlineImageNode.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LexicalNestedComposer } from '@lexical/react/LexicalNestedComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $createNodeSelection,
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DRAGSTART_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useModal from '../../hooks/useModal.jsx';
import LinkPlugin from '../../plugins/LinkPlugin/index.js';
import Button from '../../ui/Button.jsx';
import { DialogActions } from '../../ui/Dialog.jsx';
import ImageResizer from '../../ui/ImageResizer.jsx';
import Select from '../../ui/Select.jsx';
import TextInput from '../../ui/TextInput.jsx';
import ContentEditables from '../../ui/ContentEditable.jsx';
import { $isInlineImageNode } from './InlineImageNode';

// Render image directly without Suspense so it appears as soon as the browser
// loads it. Previously useSuspenseImage threw a Promise and Suspense fallback
// was null; when the promise resolved Lexical did not re-render decorators, so
// the image only appeared after a click (selection change triggered re-render).
function InlineImageImg({
  altText,
  className,
  imageRef,
  src,
  width,
  height,
  position,
  resizeWidth,
  resizeHeight,
}) {
  const resolvedWidth = resizeWidth != null ? resizeWidth : (width === 'inherit' ? 'auto' : width);
  const resolvedHeight = resizeHeight != null ? resizeHeight : (height === 'inherit' ? 'auto' : height);

  return (
    <img
      className={className || undefined}
      src={src}
      alt={altText}
      ref={imageRef}
      data-position={position}
      style={{
        display: 'block',
        height: resolvedHeight,
        width: resolvedWidth,
        maxWidth: '100%',
      }}
      draggable="false"
    />
  );
}

// Alias for backwards compatibility (e.g. hot-reload or cached bundles).
const LazyImage = InlineImageImg;

export function UpdateInlineImageDialog({
  activeEditor,
  nodeKey,
  onClose,
}) {
  const editorState = activeEditor.getEditorState();
  const node = editorState.read(
    () => $getNodeByKey(nodeKey),
  );
  const [altText, setAltText] = useState(node.getAltText());
  const [showCaption, setShowCaption] = useState(node.getShowCaption());
  const [position, setPosition] = useState(node.getPosition());

  const handleShowCaptionChange = (e) => {
    setShowCaption(e.target.checked);
  };

  const handlePositionChange = (e) => {
    setPosition(e.target.value);
  };

  const handleOnConfirm = () => {
    const payload = { altText, position, showCaption };
    if (node) {
      activeEditor.update(() => {
        // Update the node properties
        node.update(payload);

        // Ensure the node remains selected after update to trigger proper history tracking
        const nodeKey = node.getKey();
        const selection = $createNodeSelection();
        selection.add(nodeKey);
        $setSelection(selection);
        
        // Force a selection change to ensure history records this operation
        activeEditor.dispatchCommand(SELECTION_CHANGE_COMMAND, activeEditor);
      });
    }
    onClose();
  };

  return (
    <>
      <div style={{ marginBottom: '1em' }}>
        <TextInput
          label="Alt Text"
          placeholder="Descriptive alternative text"
          onChange={setAltText}
          value={altText}
          data-test-id="image-modal-alt-text-input"
        />
      </div>

      <Select
        style={{ marginBottom: '1em', width: '208px' }}
        value={position}
        label="Position"
        name="position"
        id="position-select"
        onChange={handlePositionChange}>
        <option value="left">Left</option>
        <option value="right">Right</option>
        <option value="full">Full Width</option>
      </Select>

      <div className="Input__wrapper">
        <input
          id="caption"
          type="checkbox"
          checked={showCaption}
          onChange={handleShowCaptionChange}
        />
        <label htmlFor="caption">Show Caption</label>
      </div>

      <DialogActions>
        <Button
          data-test-id="image-modal-file-upload-btn"
          onClick={() => handleOnConfirm()}>
          Confirm
        </Button>
      </DialogActions>
    </>
  );
}

export default function InlineImageComponent({
  src,
  altText,
  nodeKey,
  width,
  height,
  showCaption,
  caption,
  position,
}) {
  const [modal, showModal] = useModal();
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const activeEditorRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDimensions, setResizeDimensions] = useState(null);
  const isEditable = useLexicalEditable();

  const isInNodeSelection = useMemo(
    () =>
      isSelected &&
      editor.getEditorState().read(() => {
        const sel = $getSelection();
        return $isNodeSelection(sel) && sel.has(nodeKey);
      }),
    [editor, isSelected, nodeKey],
  );

  const onResizeEnd = (nextWidth, nextHeight) => {
    // Delay hiding the resize bars for click case
    setTimeout(() => {
      setIsResizing(false);
      setResizeDimensions(null);
    }, 200);

    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isInlineImageNode(node)) {
        // Update the node dimensions
        node.setWidthAndHeight(nextWidth, nextHeight);
        
        // Ensure the node remains selected to trigger proper history tracking
        const selection = $createNodeSelection();
        selection.add(nodeKey);
        $setSelection(selection);
        
        // Force a selection change to ensure history records this operation
        editor.dispatchCommand(SELECTION_CHANGE_COMMAND, editor);
      }
    });
  };

  const onResizeStart = () => {
    setIsResizing(true);
  };

  const $onEnter = useCallback(
    (event) => {
      const latestSelection = $getSelection();
      const buttonElem = buttonRef.current;
      if (
        isSelected &&
        $isNodeSelection(latestSelection) &&
        latestSelection.getNodes().length === 1
      ) {
        if (showCaption) {
          // Move focus into nested editor
          $setSelection(null);
          event.preventDefault();
          caption.focus();
          return true;
        } else if (
          buttonElem !== null &&
          buttonElem !== document.activeElement
        ) {
          event.preventDefault();
          buttonElem.focus();
          return true;
        }
      }
      return false;
    },
    [caption, isSelected, showCaption],
  );

  const $onEscape = useCallback(
    (event) => {
      if (
        activeEditorRef.current === caption ||
        buttonRef.current === event.target
      ) {
        $setSelection(null);
        editor.update(() => {
          setSelected(true);
          const parentRootElement = editor.getRootElement();
          if (parentRootElement !== null) {
            parentRootElement.focus();
          }
        });
        return true;
      }
      return false;
    },
    [caption, editor, setSelected],
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor;
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === imageRef.current) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const event = payload;
          if (isResizing) {
            return true;
          }
          if (event.target === imageRef.current) {
            if (event.shiftKey) {
              setSelected(!isSelected);
            } else {
              clearSelection();
              setSelected(true);
            }
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_ENTER_COMMAND, $onEnter, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ESCAPE_COMMAND, $onEscape, COMMAND_PRIORITY_LOW),
    );
  }, [
    clearSelection,
    editor,
    isResizing,
    isSelected,
    $onEnter,
    $onEscape,
    setSelected,
  ]);

  const draggable = isInNodeSelection && !isResizing;
  const isFocused = (isSelected || isResizing) && isEditable;
  return (
    <>
      <span draggable={draggable}>
        {isEditable && (
          <button
            className="image-edit-button"
            ref={buttonRef}
            type="button"
            onClick={() => {
              showModal('Update Inline Image', (onClose) => (
                <UpdateInlineImageDialog
                  activeEditor={editor}
                  nodeKey={nodeKey}
                  onClose={onClose}
                />
              ));
            }}>
            Edit
          </button>
        )}
        <InlineImageImg
          className={
            isFocused
              ? `focused ${isInNodeSelection ? 'draggable' : ''}`
              : null
          }
          src={src}
          altText={altText}
          imageRef={imageRef}
          width={width}
          height={height}
          position={position}
          resizeWidth={resizeDimensions?.width}
          resizeHeight={resizeDimensions?.height}
        />
      </span>
      {showCaption && (
        <span className="image-caption-container">
          <LexicalNestedComposer initialEditor={caption}>
            <LinkPlugin />
            <RichTextPlugin
              contentEditable={
                <ContentEditables
                  placeholder="Enter a caption..."
                  placeholderClassName="InlineImageNode__placeholder"
                  className="InlineImageNode__contentEditable"
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </LexicalNestedComposer>
        </span>
      )}
      {isInNodeSelection && isFocused && (
        <ImageResizer
          editor={editor}
          buttonRef={buttonRef}
          imageRef={imageRef}
          onResizeStart={onResizeStart}
          onResizeEnd={onResizeEnd}
          onResize={(w, h) => setResizeDimensions({ width: w, height: h })}
          captionsEnabled={false}
          showCaption={showCaption}
          setShowCaption={() => { }}
        />
      )}
      {modal}
    </>
  );
}
